import React, { useState, useEffect } from 'react';
import { X, Trophy } from 'lucide-react';

interface HeadToHeadModalProps {
  player1Id: string;
  player2Id: string;
  player1Name: string;
  player2Name: string;
  onClose: () => void;
}

export const HeadToHeadModal: React.FC<HeadToHeadModalProps> = ({
  player1Id,
  player2Id,
  player1Name,
  player2Name,
  onClose
}) => {
  const [loading, setLoading] = useState(true);
  const [h2hData, setH2hData] = useState<any>(null);

  useEffect(() => {
    const fetchH2H = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL || "https://techtennis-api.vercel.app"}/api/users/h2h/${player1Id}/${player2Id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('tt_auth_token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          setH2hData(data);
        }
      } catch (err) {
        console.error("Error fetching H2H", err);
      } finally {
        setLoading(false);
      }
    };
    fetchH2H();
  }, [player1Id, player2Id]);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1100,
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1A112C', borderRadius: '12px',
        width: '100%', maxWidth: '600px', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', color: 'white',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Trophy size={24} color="#3A8E58" />
            Head to Head
          </h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', zIndex: 10, padding: '4px' }}>
            <X size={28} color="white" strokeWidth={3} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {loading ? (
            <p style={{ textAlign: 'center' }}>Carregando histórico...</p>
          ) : !h2hData ? (
            <p style={{ textAlign: 'center' }}>Erro ao carregar H2H.</p>
          ) : (
            <>
              {/* Placar Gigante */}
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '30px', background: 'rgba(0,0,0,0.3)', padding: '20px', borderRadius: '8px' }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: 'white' }}>{player1Name}</h3>
                  <div style={{ fontSize: '48px', fontWeight: 'bold' }}>{h2hData.player1Wins}</div>
                </div>
                <div style={{ fontSize: '32px', fontWeight: 'bold', margin: '0 20px', color: 'rgba(255,255,255,0.3)' }}>X</div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: 'white' }}>{player2Name}</h3>
                  <div style={{ fontSize: '48px', fontWeight: 'bold' }}>{h2hData.player2Wins}</div>
                </div>
              </div>

              {/* Lista de Jogos */}
              <h4 style={{ marginBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '8px' }}>Histórico de Partidas</h4>
              {h2hData.matches.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>Nenhuma partida finalizada entre esses jogadores.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {h2hData.matches.map((m: any) => {
                    const isPlayer1Winner = m.winner_id === player1Id;
                    return (
                      <div key={m.id} style={{ 
                        background: 'rgba(255,255,255,0.05)', 
                        padding: '12px', 
                        borderRadius: '8px',
                        borderLeft: `4px solid ${isPlayer1Winner ? '#3A8E58' : '#e74c3c'}`
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontWeight: 'bold', color: 'white' }}>{m.comp_name || 'Amistoso'}</span>
                          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                            {m.date ? new Date(m.date).toLocaleDateString('pt-BR') : ''}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '14px' }}>
                            {isPlayer1Winner ? `Vitória de ${player1Name}` : `Vitória de ${player2Name}`}
                          </span>
                          <span style={{ fontWeight: 'bold', background: 'rgba(0,0,0,0.5)', padding: '4px 8px', borderRadius: '4px' }}>
                            {m.score || 'W.O.'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
